import { ReactNode, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { RoleUpgradePrompt } from './RoleBadge';
import { AccessRequestModal } from './AccessRequestModal';

/**
 * Role-protected action wrapper component
 *
 * @description Wraps UI elements that require specific roles or permissions.
 * Provides consistent handling of access control throughout the application.
 */
export function RoleProtectedAction({
	children,
	requiredRole,
	requiredPermission,
	fallback,
	showUpgradePrompt = false,
	action = 'perform this action',
	className = '',
}: {
	/** Content to render when user has access */
	children: ReactNode;
	/** Required role for access */
	requiredRole?: string;
	/** Required permission for access */
	requiredPermission?: string;
	/** Custom fallback component */
	fallback?: ReactNode;
	/** Show upgrade prompt instead of hiding */
	showUpgradePrompt?: boolean;
	/** Description of the action for upgrade prompt */
	action?: string;
	/** Additional CSS classes */
	className?: string;
}) {
	const { user, hasRole, hasPermission, isAdmin } = useAuth();
	const [showAccessRequestModal, setShowAccessRequestModal] = useState(false);

	// Check access - ADMIN users have access to everything
	const hasAccess =
		isAdmin() ||
		(requiredRole && hasRole(requiredRole)) ||
		(requiredPermission && hasPermission(requiredPermission)) ||
		(!requiredRole && !requiredPermission);

	// If user has access, render children
	if (hasAccess) {
		return <div className={className}>{children}</div>;
	}

	// If custom fallback provided, use it
	if (fallback) {
		return <div className={className}>{fallback}</div>;
	}

	// If upgrade prompt requested, show it
	if (showUpgradePrompt && user) {
		const currentRole = user.roles?.[0] || 'READ';
		return (
			<div className={className}>
				<RoleUpgradePrompt
					currentRole={currentRole}
					requiredRole={requiredRole || 'WRITE'}
					action={action}
					onRequestAccess={() => setShowAccessRequestModal(true)}
				/>
				
				{/* Access Request Modal */}
				<AccessRequestModal
					isOpen={showAccessRequestModal}
					onClose={() => setShowAccessRequestModal(false)}
					currentRole={currentRole}
					requiredRole={requiredRole || 'WRITE'}
					action={action}
				/>
			</div>
		);
	}

	// Default: hide the action
	return null;
}

/**
 * Role-protected button component
 *
 * @description Button that automatically handles role-based access control
 * with appropriate visual feedback.
 */
export function RoleProtectedButton({
	children,
	requiredRole,
	requiredPermission,
	onClick,
	variant = 'primary',
	size = 'md',
	disabled = false,
	showTooltip = true,
	className = '',
	...props
}: {
	children: ReactNode;
	requiredRole?: string;
	requiredPermission?: string;
	onClick?: () => void;
	variant?: 'primary' | 'secondary' | 'danger' | 'outline';
	size?: 'sm' | 'md' | 'lg';
	disabled?: boolean;
	showTooltip?: boolean;
	className?: string;
	[key: string]: any;
}) {
	const { user, hasRole, hasPermission, isAdmin } = useAuth();

	// Check access - ADMIN users have access to everything
	const hasAccess =
		isAdmin() ||
		(requiredRole && hasRole(requiredRole)) ||
		(requiredPermission && hasPermission(requiredPermission)) ||
		(!requiredRole && !requiredPermission);

	// Button variants
	const variants = {
		primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
		secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500',
		danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
		outline:
			'border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-blue-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700',
	};

	const sizes = {
		sm: 'px-3 py-1.5 text-sm',
		md: 'px-4 py-2 text-sm',
		lg: 'px-6 py-3 text-base',
	};

	const disabledStyles = 'opacity-50 cursor-not-allowed';
	const enabledStyles = 'transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';

	const isDisabled = disabled || !hasAccess;
	const buttonTitle = !hasAccess
		? `Requires ${requiredRole || requiredPermission} role`
		: undefined;

	return (
		<button
			onClick={hasAccess ? onClick : undefined}
			disabled={isDisabled}
			title={showTooltip ? buttonTitle : undefined}
			className={`
        inline-flex items-center rounded-md font-medium
        ${variants[variant]}
        ${sizes[size]}
        ${isDisabled ? disabledStyles : enabledStyles}
        ${className}
      `}
			{...props}
		>
			{children}
		</button>
	);
}

/**
 * Permission tooltip component
 * Shows permission requirements when hovering over disabled elements
 */
export function PermissionTooltip({
	children,
	requiredRole,
	requiredPermission,
	action = 'perform this action',
}: {
	children: ReactNode;
	requiredRole?: string;
	requiredPermission?: string;
	action?: string;
}) {
	const { hasRole, hasPermission, isAdmin } = useAuth();

	const hasAccess =
		isAdmin() ||
		(requiredRole && hasRole(requiredRole)) ||
		(requiredPermission && hasPermission(requiredPermission));

	if (hasAccess) {
		return <>{children}</>;
	}

	return (
		<div className="group relative inline-block">
			<div className="opacity-50 cursor-not-allowed">{children}</div>
			<div className="invisible group-hover:visible absolute z-10 w-48 p-2 mt-1 text-xs text-white bg-gray-900 rounded-lg shadow-lg dark:bg-gray-700 -translate-x-1/2 left-1/2">
				<div className="font-medium mb-1">Permission Required</div>
				<div className="text-gray-300">
					{requiredRole && `Requires ${requiredRole} role to ${action}`}
					{requiredPermission && `Requires ${requiredPermission} permission to ${action}`}
				</div>
				<div className="absolute -top-1 left-1/2 w-2 h-2 bg-gray-900 rotate-45 transform -translate-x-1/2 dark:bg-gray-700"></div>
			</div>
		</div>
	);
}
